
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Camera, X, Sparkles, Loader2, Image as ImageIcon, CheckCircle2, AlertTriangle, Map as MapIcon, Calendar, FileText, Info, ThumbsUp, Edit2, Trash2, Users, Zap, ChartPie, History, Undo2, WifiOff, Crosshair } from 'lucide-react';
import { compressImage, HEIC_ACCEPT, bildFehlerText } from '../../services/imageService';
import { generateSafetyText, KI_NICHT_ERREICHBAR } from '../../services/geminiService';
import { InspectionItem, Contact, ProjectPlan, ReportLanguage, ProjektVerortung } from '../../types';
import { freimeldeDatum } from '../../services/inspectionItemStatus';
import { ImageEditor } from '../ImageEditor';
import { registriereZurueck } from '../../services/appUmfang';
import { standortVerfuegbar } from '../../services/standortSensoren';
import {
  autoOrtAusLive, autoOrtAusVorschlag, ebeneBestaetigen, eintragVerortung, geruestLagen, geschossEbenen, ortstextUebernehmen,
  planFuerEbene, useVerortungGeraet, useVerortungsSitzung, verortungDiagnose, verortungLesen, verortungNutzbar,
  type AutoOrt, type Ebene, type Ortsvorschlag, type SensorSchnappschuss,
} from '../../services/verortung';
import { istNativeApp } from '../../services/nativePlatform';
import { OrtAutomatik } from '../verortung/OrtAutomatik';
import { VerortungsKamera, type KameraAufnahme } from '../verortung/VerortungsKamera';
import { OrtsvorschlagKarte } from '../verortung/OrtsvorschlagKarte';
import { useLiveStandort } from '../verortung/useLiveStandort';
import { liveEbene, liveEintragVerortung, type LivePunkt } from '../../services/verortung/liveStandort';
import { PlanVerortungDialog } from '../plaene/PlanVerortungDialog';
import { hatPins, pinEntfernen, pinHinzufuegen, pinsVon, planIdsVon, sensorPinEntfernen, sensorPinSetzen, type PlanPin } from '../../services/planPins';
import { MangelKatalogAuswahl, type KatalogSteuerung } from '../mangelKatalog/MangelKatalogAuswahl';
import { KatalogChip } from '../mangelKatalog/KatalogChip';
import { startPfadVon } from '../mangelKatalog/auswahlAnsicht';
import { katalogStand } from '../../services/mangelKatalog/store';
import { katalogSprache } from '../../services/mangelKatalog/sprache';
import { vorbelegungAus } from '../../services/mangelKatalog/vorbelegung';
import type { KatalogAuswahl } from '../../services/mangelKatalog/typen';
import {
  angabeFehlt, felderAusVorbelegung, katalogFelderFuerEintrag, katalogFelderLoesen,
  fristNachTypwahl, katalogImAssistenten, schrittNachTypwahl, schrittVorDetails,
} from './wizardKatalog';
import {
  entwurfUnberuehrt, FOTO_LOESCHEN_FRIST_MS, fotoGrenze, fotoGrenzeText, fotoLoeschTipp, fotoZaehlerText, istZusage,
  KEINE_VORIGEN_ANGABEN, KI_ZEITGRENZE_MS, kiAntwortGilt, kiFelderVon, kiFuerTyp, kiRueckgaengigAnwenden, kiVorschlagAnwenden,
  mitZeitgrenze, nichtGespeichertText, schliessenAktion, darfSchliessen, SCHLIESSEN_NOTAUSGANG_MS, speicherFehlerBericht, speicherStandNach, speicherText, useNetzDa,
  vorigeAngabenNach, wieZuvor, ZEIT_ABGELAUFEN, type KiFelder, type SpeicherStand, type VorigeAngaben,
  ENTWURF_ENTPRELLEN_MS, entwurfAktion, entwurfAusZustand, entwurfBeschreibung, entwurfPruefen, entwurfWiederherstellen, entwurfZeitText,
  phaseNachLaden, statusFuerTyp, type AssistentEntwurf, type AssistentSchritt, type EntwurfPhase, type EntwurfZustand,
} from './wizardLogik';
import { assistentEntwurfSchluessel, ENTWURF_UNLESBAR, entwurfLaden, entwurfLoeschen, entwurfSpeichern } from '../../services/assistentEntwurf';

/** Wie am Desktop: hoechstens drei Bilder je Eintrag. */
const MAX_PHOTOS = 3;
/** Zustaendigkeit „alle Firmen" — als Text gespeichert, wie jede andere Firma. */
const ALLE_FIRMEN = 'Alle';

interface Props {
  /**
   * Nimmt den Eintrag OHNE laufende Nummer entgegen: die vergibt erst der
   * Aufrufer beim Schreiben (`neuenEintragEinfuegen`). Hier wäre sie zwingend
   * veraltet — geschrieben wird erst nach dem Foto-Upload, und der nächste
   * Eintrag stünde schon in der Maske. Der Wizard zeigt deshalb sofort
   * „Erfasst – wird gespeichert"; liefert der Aufrufer eine Zusage, erst nach
   * ihr „Gespeichert" (bzw. „Nicht gespeichert").
   */
  onSave: (item: InspectionItem) => void | Promise<void>;
  onCancel: () => void;
  contacts: Contact[];
  plans?: ProjectPlan[]; // New: Pass plans
  initialData?: Partial<InspectionItem>; // Vorbefüllung (z.B. n.i.O.-Prüfpunkt → Mangel)
  reportDate?: string; // Begehungsdatum: Feststellungs-/Erledigungsdatum statt "heute"
  /**
   * Gesetzt, sobald das Projekt während der Erfassung dichtgemacht hat (fremde
   * Projektsperre). Der Assistent bleibt dann bewusst stehen — sonst wäre die
   * bereits getippte Eingabe mit dem Aushängen der Komponente weg —, nimmt
   * aber nichts mehr entgegen: Speichern gesperrt, Grund als roter Balken.
   * Text und Regel: services/mobileWizardLock.ts.
   */
  saveBlockedNotice?: string | null;
  /** Rolle fuer den Textvorschlag (SiGeKo- oder HSE-Wortlaut). */
  appMode?: 'sigeko' | 'hse';
  /**
   * Berichtssprache. Sie bestimmt die Sprache des Textvorschlags — wie am
   * Desktop (InspectionList). Fehlte sie, kam in einem englischen Bericht aus
   * einem englischen Stichwort ein deutscher Vorschlag zurueck.
   */
  language?: ReportLanguage;
  /**
   * Offene Maengel des Berichts. Die Freimeldung bietet sie zur Auswahl an
   * und schliesst den gewaehlten Eintrag ueber `onCloseItem` — statt einen
   * neuen, bezugslosen „Erledigt"-Eintrag anzulegen.
   */
  openItems?: InspectionItem[];
  onCloseItem?: (id: string, felder: { statusComment: string; closedImage?: string }) => void | Promise<void>;
  /**
   * Sensor-Verortung des Projekts (`metadata.verortung`, roh). „Kamera" oeffnet
   * immer die Systemkamera (Betreiber 29.10.). Nur in der App mit Sensor-Plugin,
   * wenn Geraet UND Projekt sie nicht abgeschaltet haben, steht daneben
   * „App-Kamera" — die eingebaute Kamera mit Einblendung
   * (docs/verortung-kamera-vertrag.md).
   */
  verortung?: ProjektVerortung;
  /** Schluessel der Verortungs-Sitzung (Nullpunkt je Begehung), z. B. die Projekt-ID. */
  sitzungSchluessel?: string;
  /**
   * Plan direkt aus „Auf Plan markieren“ hochladen (docs/planverortung-vertrag.md):
   * der Aufrufer haengt den neuen Plan an `metadata.plans` an. Nur zusammen mit
   * `projectId` (Upload-Ziel); fehlt eins, gibt es kein Hochladen im Assistenten.
   */
  onPlanAnlegen?: (plan: ProjectPlan) => void;
  projectId?: string;
  /** Abschnitt des Berichts (nicht 'all'): neue Plaene gehoeren dazu, die Planwahl zeigt nur seine Plaene. */
  sectionId?: string;
}

type Step = AssistentSchritt;
type ItemType = 'defect' | 'info' | 'good' | 'clearance' | 'general';

// Standardfrist: nur der Mangel bekommt sie vorbelegt (+14 Tage).
const defaultDeadline = () => new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

export const MobileInspectionWizard: React.FC<Props> = ({ onSave, onCancel, contacts, plans = [], initialData, reportDate, saveBlockedNotice = null, appMode = 'sigeko' as 'sigeko' | 'hse', language = 'de', openItems = [], onCloseItem, verortung, sitzungSchluessel, onPlanAnlegen, projectId, sectionId }) => {
  const [step, setStepRoh] = useState<Step>(initialData ? 'what' : 'type');
  const [itemType, setItemType] = useState<ItemType>('defect');
  const [photos, setPhotos] = useState<string[]>([]);
  // Index des Bildes, das gerade im Editor liegt (Pfeile, Kreise, Zuschnitt).
  const [editingPhotoIndex, setEditingPhotoIndex] = useState<number | null>(null);
  // Foto, dessen „Löschen“ gerade nachfragt („Wirklich löschen?“, verfaellt nach ~3 s).
  const [fotoLoeschFrage, setFotoLoeschFrage] = useState<number | null>(null);
  // Textvorschlag laeuft: Knopf gesperrt, Spinner statt Funken, darunter „Abbrechen“.
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState('');
  // Werte vor dem uebernommenen Vorschlag: einstufiges „Rückgängig“ bis zur
  // naechsten Eingabe in Titel/Beschreibung oder zum Schrittwechsel.
  const [kiVorher, setKiVorher] = useState<KiFelder | null>(null);
  // Laufnummer des Vorschlags: Schrittwechsel, Tippen und „Abbrechen“ entwerten eine noch laufende Anfrage.
  const kiLaufRef = useRef(0);
  const netzDa = useNetzDa();
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  // Leiste „Eintrag verwerfen?“ (X oben, „Abbrechen“, Zurueck-Taste im ersten Schritt).
  const [verwerfenFrage, setVerwerfenFrage] = useState(false);
  // Serienerfassung: Ort und Firma des vorigen Eintrags fuer den Chip „wie zuvor“.
  const [vorige, setVorige] = useState<VorigeAngaben>(KEINE_VORIGEN_ANGABEN);
  // Stand des letzten Speicherns (Schritt „done“); die Laufnummer haelt eine spaete Zusage
  // davon ab, den Stand eines neueren Eintrags zu ueberschreiben.
  const [speicherStand, setSpeicherStand] = useState<SpeicherStand>('erfasst');
  const speicherLaufRef = useRef(0);
  // Jede gescheiterte Zusage (auch nach „Nächster Mangel“): roter Balken, bis er weggetippt wird.
  const [nichtGespeichert, setNichtGespeichert] = useState<string[]>([]);
  // Laufende Speicherungen (Zusagen des Aufrufers). Solange eine laeuft, schliesst
  // der Assistent nicht: das Modul hinge sonst aus, und das Ergebnis kaeme nur
  // noch auf Umwegen an. Nach dem Tipp schliesst er von selbst, sobald alles
  // gespeichert ist; nach SCHLIESSEN_NOTAUSGANG_MS gibt es einen Notausgang.
  const [laufendeSpeicherungen, setLaufendeSpeicherungen] = useState(0);
  const [schliessenGewuenscht, setSchliessenGewuenscht] = useState(false);
  const [notausgang, setNotausgang] = useState(false);

  // ─── Lokaler Entwurf (services/assistentEntwurf.ts, Regeln in wizardLogik.ts) ──
  // Android beendet die App gern, waehrend die Systemkamera offen ist; eine neu
  // geladene Seite faengt von vorn an. Der Stand liegt deshalb zusaetzlich als
  // Entwurf im Geraet und wird beim naechsten Oeffnen angeboten.
  // Schluessel beim Oeffnen gefasst: kippt der Schreibschutz mitten in der
  // Erfassung (projectId faellt dann weg), wird die Eingabe weiter lokal gesichert.
  const [entwurfSchluessel] = useState<string | null>(() => assistentEntwurfSchluessel(projectId, sectionId));
  // Mit Vorbefuellung geoeffnet (n.i.O., gezielte Fluesse): kein Angebot, ein liegender Entwurf bleibt unangetastet.
  const [mitVorbefuellung] = useState(() => !!initialData);
  // Wem der Speicher gerade gehoert (Phasen: wizardLogik.EntwurfPhase).
  const [entwurfPhase, setEntwurfPhase] = useState<EntwurfPhase>(() => (entwurfSchluessel ? 'pruefen' : 'aus'));
  // Leiste „Nicht gespeicherter Eintrag von …“ (Zeit beim Lesen gefasst, nie im Render).
  const [entwurfAngebot, setEntwurfAngebot] = useState<{ entwurf: AssistentEntwurf; zeit: string } | null>(null);
  // Laufnummer: Uebergabe und „Verwerfen“ zaehlen weiter — ein noch wartendes Entprellen schreibt dann nichts mehr.
  const entwurfLaufRef = useRef(0);

  /** Laufenden Vorschlag verwerfen (Tippen, „Abbrechen“, Schrittwechsel): seine spaete Antwort schreibt nichts mehr. */
  const kiAbbrechen = () => {
      kiLaufRef.current += 1;
      setIsSuggesting(false);
  };

  /**
   * Jeder Schrittwechsel beendet das „Rückgängig“ des KI-Vorschlags und die
   * Rueckfrage „Eintrag verwerfen?“; ein noch laufender Vorschlag schreibt
   * danach nichts mehr in die Maske.
   */
  const setStep = (s: Step) => {
      kiAbbrechen();
      setKiVorher(null);
      setSuggestError('');
      setVerwerfenFrage(false);
      setStepRoh(s);
  };

  // Der Mangel, der freigemeldet wird. Gesetzt nur ueber die Auswahlliste;
  // null heisst: Freimeldung ohne Bezug (alter Weg, neuer Eintrag).
  const [clearanceTarget, setClearanceTarget] = useState<InspectionItem | null>(null);
  // Merkt sich, ob die Frist vom Nutzer stammt. Nur dann bleibt sie beim
  // Wechsel des Typs stehen — die reine Vorbelegung wird verworfen.
  const [deadlineTouched, setDeadlineTouched] = useState(!!initialData?.deadline);

  // ─── Standardmangel per Torte (docs/mangelkatalog-plan.md) ─────────────────
  // In jeder Berichtssprache (englisch nur bei 'en'), aber ohne Vorbefuellung
  // (n.i.O.-Uebergabe). Die Vorbefuellung gilt nur fuer den ersten Eintrag:
  // nach dem Speichern („Nächster Eintrag“) ist der Katalog wieder da.
  const [vorbefuellungVerbraucht, setVorbefuellungVerbraucht] = useState(false);
  const katalogAktiv = katalogImAssistenten(language, vorbefuellungVerbraucht ? undefined : initialData);
  // Weg beim Mangel, schon auf dem Typ-Bildschirm gewählt (Betreiber 28.09.):
  // „Standardmangel“ (Torte) oder „Eigener Text / KI“ (direkt „Details“).
  const [mitKatalog, setMitKatalog] = useState(true);
  const katalogWeg = katalogAktiv && mitKatalog;
  const erfassungsSprache = katalogSprache(language);
  // Gewaehlter Standardmangel (Chip in „Details“).
  const [katalogAuswahl, setKatalogAuswahl] = useState<KatalogAuswahl | null>(null);
  // Startpfad der Auswahl: sie liest ihn nur beim Einhaengen, daher je Oeffnen ein neuer key (lauf).
  const [katalogStart, setKatalogStart] = useState<{ pfad: string[]; lauf: number }>({ pfad: [], lauf: 0 });
  // Zurueck-Taste im Katalog: erst Ebene fuer Ebene (zuerst eine offene ⓘ), in der Wurzel zu „Typ“.
  const katalogSteuerungRef = useRef<KatalogSteuerung | null>(null);

  const [data, setData] = useState<Partial<InspectionItem>>({
    title: '',
    observation: '',
    location: '',
    responsiblePerson: '',
    status: 'open',
    planId: undefined,
    planCoordinates: undefined,
    deadline: defaultDeadline(),
    ...initialData
  });

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // ─── Sensor-Verortung (Kamera mit Einblendung) ─────────────────────────────
  // Drei Schalter, jeder kann alles abschalten: Geraet (App-Info), Projekt
  // (Plaene → Verortung), Einblendung in der Kamera. Ohne App/Plugin: nie.
  const [geraetVerortung] = useVerortungGeraet();
  const projektVerortung = useMemo(() => verortungLesen(verortung), [verortung]);
  const [sensorApp] = useState<boolean>(standortVerfuegbar);
  const kameraMitVerortung = verortungNutzbar(geraetVerortung, projektVerortung, sensorApp);
  const verortungsSchluessel = sitzungSchluessel || 'ohne-projekt';
  const [verortungsSitzung, setVerortungsSitzung] = useVerortungsSitzung(verortungsSchluessel);
  const [kameraOffen, setKameraOffen] = useState(false);
  // Letzter Vorschlag aus der eingebauten Kamera (mit dem Schnappschuss zum Neu-Nullen).
  const [ortsvorschlag, setOrtsvorschlag] = useState<{ vorschlag: Ortsvorschlag; schnappschuss: SensorSchnappschuss } | null>(null);

  // Live-Standort: blinkender Punkt in der Planwahl, Geschoss-Schaetzung fuer den automatischen Ort.
  // Sensoren laufen nur waehrend der Erfassung (ab „Details“ bis „Wo“) und nur, wenn alles an ist.
  const erfassungOffen = step === 'what' || step === 'who' || step === 'where';
  const live = useLiveStandort({ offen: showPlanPicker || erfassungOffen, verortung: projektVerortung, plaene: plans, sitzungSchluessel: verortungsSchluessel });

  // ─── Automatischer Ort (Betreiber 27.09.: nicht mehr eintippen muessen) ────
  // Gefuellt wird nur ein LEERES Ortsfeld ohne Pin; Anzeige „automatisch“ + „Rueckgaengig“.
  const [appNativ] = useState<boolean>(istNativeApp);
  const diagnose = verortungDiagnose({ app: appNativ, plugin: sensorApp, geraet: geraetVerortung, projekt: projektVerortung });
  // pinAuto: der aktuelle Pin stammt aus der Automatik (jede Pin-Aktion von Hand beendet den Auto-Zustand).
  const [autoOrt, setAutoOrt] = useState<{ text: string; ebene: Ebene | null; schnappschuss: SensorSchnappschuss | null; quellen: string[]; pinAuto: boolean } | null>(null);
  const autoAktiv = !!autoOrt && data.location === autoOrt.text;

  const autoAnwenden = (a: AutoOrt, schnapp: SensorSchnappschuss | null): boolean => {
      if ((data.location || '').trim() || hatPins(data)) return false;
      const planName = a.planId ? plans.find(p => p.id === a.planId)?.name : undefined;
      const text = ortstextUebernehmen('', a.ebene) || planName || '';
      if (!text) return false;
      const { pin, planId } = a;
      setData(prev => ({
          ...prev,
          location: text,
          // Sensor-Nadel (planPins): ohne Nadel steht sie allein.
          ...(pin && planId ? sensorPinSetzen(prev, { x: pin.x, y: pin.y, planId }) : {}),
          verortung: a.eintrag,
      }));
      setAutoOrt({ text, ebene: a.ebene, schnappschuss: schnapp, quellen: a.quellen, pinAuto: !!(a.pin && a.planId) });
      return true;
  };

  /** Weiter zum Schritt „Wo“ — dort steht der Ort dann schon, wenn die Sensoren ihn kennen. */
  const zuOrtSchritt = () => {
      setStep('where');
      if (!live.aktiv) return;
      const a = autoOrtAusLive({ verortung: projektVerortung, plaene: plans, schaetzung: live.schaetzung, schnappschuss: live.schnappschuss });
      if (a) autoAnwenden(a, live.schnappschuss);
  };

  const autoRueckgaengig = () => {
      // Nur die automatische (Sensor-)Nadel geht — Hand-Nadeln beenden den Auto-Zustand ohnehin.
      setData(prev => ({ ...prev, location: '', ...sensorPinEntfernen(prev), verortung: undefined }));
      setAutoOrt(null);
  };

  /** Geschoss-Knopf im Schritt „Wo“: Ort setzen UND Nullpunkt (Bestaetigung „hier bin ich“). */
  const ebeneTippen = (ebene: Ebene) => {
      const basis = autoAktiv ? '' : (data.location || '');
      const pinAutomatisch = autoAktiv && !!autoOrt?.pinAuto;
      // Hand-Nadeln markieren den MANGEL — sie bleiben immer, und solange es eine gibt, setzt der
      // Geschoss-Knopf keine Nadel dazu (Codex-Review 27.09.). Ersetzt wird nur die Sensor-Nadel.
      const hatHandNadel = pinsVon(data).some(p => !p.sensor);
      const plan = planFuerEbene(projektVerortung, plans, ebene);
      const punkt = !hatHandNadel && plan && live.aktiv ? live.punktFuer(plan.id) : null;
      setData(prev => {
          // Bleibt eine fruehere Sensor-Nadel stehen, bleiben auch Blick und Genauigkeit an ihr.
          const alteSensorNadel = !punkt && !pinAutomatisch && pinsVon(prev).some(p => p.sensor);
          return {
              ...prev,
              location: ortstextUebernehmen(basis, ebene),
              // Pin aus dem Live-Punkt des Geschossplans (ersetzt die Sensor-Nadel); ein
              // automatischer Pin eines anderen Geschosses faellt weg.
              ...(punkt && plan ? sensorPinSetzen(prev, { x: punkt.pin.x, y: punkt.pin.y, planId: plan.id }) : pinAutomatisch ? sensorPinEntfernen(prev) : {}),
              // Das Geschoss gilt auch ohne Nadel — die Sensor-Nadel ist in planPins ausdruecklich
              // markiert, eine Hand-Nadel wird dadurch nicht zur Sensor-Nadel.
              verortung: {
                  quelle: 'sensor', ebene: ebene.name, hoeheM: ebene.hoeheM,
                  genauigkeitM: punkt ? punkt.genauigkeitM : alteSensorNadel ? prev.verortung?.genauigkeitM ?? null : null,
                  blickGradPlan: punkt ? punkt.blickGradPlan : alteSensorNadel ? prev.verortung?.blickGradPlan ?? null : null,
                  sicherheit: 1,
              },
          };
      });
      const druck = live.schnappschuss?.druckHpa ?? null;
      if (druck !== null) setVerortungsSitzung(ebeneBestaetigen(verortungsSitzung, ebene, druck, Date.now()));
      setAutoOrt(null);
  };

  /**
   * Live-Punkt als Sensor-Nadel uebernehmen (Standort + Blickrichtung) — zugleich Bestaetigung des
   * Geschosses. Ersetzt nur die bisherige Sensor-Nadel, Hand-Nadeln bleiben; der
   * Verortungsdialog bleibt offen (Betreiber 28.09.: heraus nur ueber „Fertig“).
   */
  const liveUebernehmen = (planId: string, punkt: LivePunkt) => {
      const pName = plans.find(p => p.id === planId)?.name || 'Plan';
      // Der gewaehlte Plan bestimmt das Geschoss (nicht die Schaetzung) — sonst Pin im 2. OG, Text „1. OG“.
      const ebene = liveEbene(projektVerortung, planId, live.schaetzung);
      setData(prev => ({
          ...prev,
          ...sensorPinSetzen(prev, { x: punkt.pin.x, y: punkt.pin.y, planId }),
          location: ortstextUebernehmen(prev.location || '', ebene) || pName,
          verortung: liveEintragVerortung(punkt, live.schaetzung, ebene),
      }));
      live.planBestaetigen(planId);
      setAutoOrt(null);
  };

  // Standard ist die Systemkamera (Betreiber 29.10.); die eingebaute Kamera mit Einblendung
  // gibt es als zweiten Knopf „App-Kamera“, solange die Verortung nutzbar ist.
  const kameraOeffnen = () => {
      // Vor der Kamera sofort sichern: genau dort beendet Android gern die App.
      entwurfSofortSichern();
      cameraInputRef.current?.click();
  };

  const appKameraOeffnen = () => {
      entwurfSofortSichern();
      setKameraOffen(true);
  };

  const kameraAufnahme = (a: KameraAufnahme) => {
      setKameraOffen(false);
      setPhotos(prev => (prev.length >= maxFotos ? prev : [...prev, a.bild]));
      const v = a.vorschlag;
      // Leeres Ortsfeld: sicherer Vorschlag wird direkt eingetragen („automatisch“ + Rueckgaengig).
      const auto = autoOrtAusVorschlag(v);
      if (auto && autoAnwenden(auto, a.schnappschuss)) { setOrtsvorschlag(null); return; }
      // Sonst (unsicher oder Ort schon da): nur als Vorschlag zeigen, wenn es etwas gibt (Ebene oder Pin).
      setOrtsvorschlag(v && (v.schaetzung?.ebene || v.pin) ? { vorschlag: v, schnappschuss: a.schnappschuss } : null);
  };

  /** Vorschlag uebernehmen: Ortstext, Plan-Pin, Ergebnis am Eintrag; die Bestaetigung nullt neu. */
  const ortsvorschlagUebernehmen = (danachAufPlan: boolean) => {
      if (!ortsvorschlag) return;
      const { vorschlag, schnappschuss } = ortsvorschlag;
      const ebene = vorschlag.schaetzung?.zwischen ? null : vorschlag.schaetzung?.ebene ?? null;
      const vorschlagPin = vorschlag.planId && vorschlag.pin ? { x: vorschlag.pin.x, y: vorschlag.pin.y, planId: vorschlag.planId } : null;
      const mitPin = !!vorschlagPin;
      setData(prev => ({
          ...prev,
          location: ortstextUebernehmen(prev.location || '', ebene),
          // Sensor-Nadel (ersetzt eine fruehere Sensor-Nadel, Hand-Nadeln bleiben).
          ...(vorschlagPin ? sensorPinSetzen(prev, vorschlagPin) : {}),
          // Ohne Pin bleibt ein vorhandener Pin (anderer Plan) stehen: dann KEIN planbezogener Blick
          // und keine Genauigkeit daran haengen (Codex-Review 27.09., P2).
          verortung: mitPin ? eintragVerortung(vorschlag) : { ...eintragVerortung(vorschlag), blickGradPlan: null, genauigkeitM: null },
      }));
      if (ebene && schnappschuss.druckHpa !== null) {
          setVerortungsSitzung(ebeneBestaetigen(verortungsSitzung, ebene, schnappschuss.druckHpa, schnappschuss.t));
      }
      setOrtsvorschlag(null);
      setAutoOrt(null);
      if (danachAufPlan) setShowPlanPicker(true);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length === 0) return;
    // Nur verkleinern und anzeigen. Die fruehere stille Bildanalyse (KI riet
    // Titel und Text aus dem Foto) ist bewusst abgeschafft: der Text kommt
    // ueber den Vorschlags-Knopf aus dem, was der Nutzer selbst eintippt.
    const remaining = maxFotos - photos.length;
    if (remaining <= 0) {
        alert(`${fotoGrenzeText(maxFotos, isTargetedClearance)}.`);
        return;
    }
    if (files.length > remaining) {
        alert(`${fotoGrenzeText(maxFotos, isTargetedClearance)} – nur ${remaining === 1 ? 'das erste Bild wird' : `die ersten ${remaining} Bilder werden`} übernommen.`);
    }
    try {
        const compressed = await Promise.all(files.slice(0, remaining).map(f => compressImage(f, 800, 0.55)));
        setPhotos(prev => [...prev, ...compressed].slice(0, maxFotos));
    } catch (err) {
        console.error('Photo upload failed', err);
        alert(bildFehlerText(err, 'de', 'Foto-Fehler.'));
    }
  };

  const removePhoto = (idx: number) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  /** „Löschen“ am Foto: erster Tipp fragt nach („Wirklich löschen?“), der zweite loescht. */
  const fotoLoeschenTippen = (idx: number) => {
      const tipp = fotoLoeschTipp(fotoLoeschFrage, idx);
      if (tipp.loeschen) removePhoto(idx);
      setFotoLoeschFrage(tipp.gefragt);
  };
  // Die Nachfrage verfaellt nach ~3 s — ein spaeterer Tipp fragt wieder.
  useEffect(() => {
      if (fotoLoeschFrage === null) return;
      const uhr = setTimeout(() => setFotoLoeschFrage(null), FOTO_LOESCHEN_FRIST_MS);
      return () => clearTimeout(uhr);
  }, [fotoLoeschFrage]);

  /**
   * Textvorschlag aus dem Stichwort im Titelfeld — dieselbe Funktion wie der
   * Desktop-Assistent (Titel, Bemerkung, Vorschrift), nur ohne eigenes Fenster.
   * Ueberschreibt nichts stillschweigend: was die KI leer laesst, bleibt stehen.
   * Scheitert er, bleiben die Felder unveraendert und die Meldung des Dienstes
   * steht darunter; gelingt er, gibt es ein einstufiges „Rückgängig“. Waehrend
   * er laeuft, bleiben Titel und Beschreibung frei: wer tippt, bricht ihn ab
   * (Tippen gewinnt), ebenso „Abbrechen“ — die spaete Antwort ueberschreibt
   * dann nichts. Nach KI_ZEITGRENZE_MS gilt die KI als nicht erreichbar.
   */
  const handleSuggestText = async () => {
      const keyword = (data.title || '').trim();
      if (!keyword || isSuggesting || !netzDa) return;
      const lauf = ++kiLaufRef.current;
      const vorher = kiFelderVon(data);
      setIsSuggesting(true);
      setSuggestError('');
      try {
          const result = await mitZeitgrenze(generateSafetyText(keyword, true, language, appMode), KI_ZEITGRENZE_MS);
          // Schritt gewechselt, getippt oder abgebrochen: nichts mehr hineinschreiben.
          if (!kiAntwortGilt(lauf, kiLaufRef.current)) return;
          if (result === ZEIT_ABGELAUFEN) {
              setSuggestError(KI_NICHT_ERREICHBAR);
              return;
          }
          setData(prev => ({ ...prev, ...kiVorschlagAnwenden(prev, result) }));
          setKiVorher(vorher);
      } catch (err) {
          if (!kiAntwortGilt(lauf, kiLaufRef.current)) return;
          setSuggestError(err instanceof Error && err.message ? err.message : KI_NICHT_ERREICHBAR);
      } finally {
          if (kiAntwortGilt(lauf, kiLaufRef.current)) setIsSuggesting(false);
      }
  };

  const kiRueckgaengig = () => {
      if (!kiVorher) return;
      setData(prev => kiRueckgaengigAnwenden(prev, kiVorher));
      setKiVorher(null);
  };

  /**
   * Schritt „Standardmangel“ oeffnen — auf der Ebene des gewaehlten Mangels,
   * sonst seiner Kategorie, sonst in der Wurzel (Chip „Ändern“, Zurueck aus
   * „Details“, Typwahl „Mangel“).
   */
  const katalogOeffnen = () => {
      const pfad = startPfadVon(katalogStand().katalog, { katalogKey: data.katalogKey, katalogKategorie: data.katalogKategorie });
      setKatalogStart(prev => ({ pfad, lauf: prev.lauf + 1 }));
      setStep('katalog');
  };

  /** Verknuepfung loesen ([×] am Chip, anderer Typ, „Freitext“): Titel, Beschreibung und Vorschrift bleiben. */
  const katalogLoesen = () => {
      setData(prev => ({ ...prev, ...katalogFelderLoesen() }));
      setKatalogAuswahl(null);
  };

  /** Standardmangel gewaehlt: Titel, Beschreibung, Vorschrift (ggf. Frist) vorbelegen, weiter zu „Details“. */
  const katalogGewaehlt = (a: KatalogAuswahl) => {
      // Datum hier im Handler, nie im Render (Frist aus der Dringlichkeit des Eintrags).
      const felder = felderAusVorbelegung(vorbelegungAus(a, new Date()), deadlineTouched);
      setData(prev => ({ ...prev, ...felder }));
      setKatalogAuswahl(a);
      setStep('what');
  };

  const handleTypeSelect = (type: ItemType, mitStandard = true) => {
      // Leiste „Nicht gespeicherter Eintrag“ offen: erst entscheiden (die Kacheln sind dann auch inert).
      if (entwurfAngebot) return;
      const bisherTyp = itemType;
      // „Eigener Text / KI“ beim Mangel: ohne Torte, eine frühere Katalogwahl fällt weg (Texte bleiben).
      const ohneKatalog = type === 'defect' && !mitStandard;
      if (type === 'defect') setMitKatalog(mitStandard);
      setItemType(type);
      // Dieselbe Zuordnung wie beim Fortsetzen eines Entwurfs.
      const status = statusFuerTyp(type);

      setData(prev => ({
          ...prev,
          status,
          // Frist nur beim Mangel vorbelegen. Eine selbst eingetragene Frist
          // bleibt erhalten, auch wenn der Typ hin und her gewechselt wird;
          // Frist und Sofortmassnahme immer gemeinsam (fristNachTypwahl).
          ...fristNachTypwahl({ typ: type, bisherTyp, deadlineTouched, bisher: prev, standardFrist: defaultDeadline() }),
          // Standardmangel gibt es nur beim Mangel: sonst faellt die Verknuepfung weg (Texte bleiben).
          ...(type !== 'defect' || ohneKatalog ? katalogFelderLoesen() : {})
      }));
      if (type !== 'defect' || ohneKatalog) setKatalogAuswahl(null);
      setClearanceTarget(null);
      // Freimeldung: erst den offenen Mangel waehlen, wenn es welche gibt.
      // Mangel: erst der Standardmangel (Torte), wenn der Katalog aktiv und gewählt ist.
      const ziel = schrittNachTypwahl(type, { katalogAktiv: katalogAktiv && !ohneKatalog, freimeldungMitAuswahl: openItems.length > 0 && !!onCloseItem });
      if (ziel === 'katalog') katalogOeffnen();
      else setStep(ziel);
  };

  const handlePickTarget = (item: InspectionItem) => {
      setClearanceTarget(item);
      setData(prev => ({ ...prev, title: item.title, observation: '' }));
      setStep('what');
  };

  const handleSave = () => {
      // Zweite Reihe hinter dem gesperrten Knopf: handleSave setzt die Maske
      // anschliessend zurueck ("Nächster Eintrag"). Liefe es hier durch,
      // waere die Eingabe geloescht — und gespeichert wurde sie nicht.
      if (saveBlockedNotice) return;
      // Freimeldung MIT Bezug: kein neuer Eintrag, der gewaehlte Mangel wird
      // geschlossen — Datum, Nummernwechsel und Felder setzt der Aufrufer
      // ueber denselben Helfer wie der Desktop (statusWechselAnwenden).
      if (itemType === 'clearance' && clearanceTarget && onCloseItem) {
          speichernVerfolgen(onCloseItem(clearanceTarget.id, {
              statusComment: (data.observation || '').trim(),
              // Der Freimelde-Beleg ist am Desktop EIN Bild — das erste.
              closedImage: photos[0],
          }), `Freimeldung: ${clearanceTarget.title || ''}`);
          resetAfterSave();
          return;
      }
      // Erfasst wird zur Begehung, nicht am Tag der Nacharbeit: Feststellungs-
      // und Erledigungsdatum kommen aus dem Bericht, wie am Desktop auch.
      // Ohne Berichtskontext bleibt „heute" der Rueckfall.
      const erfassungsDatum = freimeldeDatum(reportDate);
      // Automatischer Ort unveraendert gespeichert = bestaetigt: Nullpunkt mit dem Druck von damals nachziehen.
      if (autoAktiv && autoOrt?.ebene && autoOrt.schnappschuss && autoOrt.schnappschuss.druckHpa !== null) {
          setVerortungsSitzung(ebeneBestaetigen(verortungsSitzung, autoOrt.ebene, autoOrt.schnappschuss.druckHpa, autoOrt.schnappschuss.t));
      }
      // Bei einer Information werden Ort, Firma, Frist, Vorschrift und Verortung
      // nur ausgeblendet — nicht gelöscht. Genau wie am Desktop: wer später auf
      // Mangel zurückstellt, findet seine Eingaben wieder.
      const isGeneral = itemType === 'general';
      const status = data.status || 'open';
      const newItem: InspectionItem = {
          id: crypto.randomUUID(),
          // KEINE laufende Nummer an dieser Stelle: sie entsteht erst beim
          // Schreiben, aus dem dann aktuellen Bestand. Regel und Begruendung
          // (auch die fuer die Information) stehen in inspectionItemStatus.ts.
          title: data.title || (itemType === 'clearance' ? 'Erledigung' : isGeneral ? 'Information' : 'Mangel'),
          observation: data.observation || '',
          location: data.location || '',
          responsiblePerson: data.responsiblePerson || '',
          status,
          photos: [...photos],
          dateFound: erfassungsDatum,
          // Sofortmaßnahme: keine Frist, das Kennzeichen ersetzt das Datum.
          deadline: data.immediateAction ? '' : (data.deadline || ''),
          immediateAction: !!data.immediateAction,
          regulation: data.regulation || '',
          planId: data.planId,
          planCoordinates: data.planCoordinates,
          ...(data.verortung ? { verortung: data.verortung } : {}),
          // Standardmangel: nur beim Mangel und nur gesetzte Werte (fail-open ohne SQL 131).
          ...katalogFelderFuerEintrag(itemType, data),
          sectionId: data.sectionId
      };
      
      if (itemType === 'clearance') {
          newItem.closedDate = newItem.dateFound;
          newItem.closedImage = photos[0];
      }
      
      // Ort und Firma fuer „wie zuvor“ im naechsten Eintrag (angeboten, nicht vorbelegt).
      setVorige(prev => vorigeAngabenNach(prev, newItem));
      speichernVerfolgen(onSave(newItem), newItem.title);
      resetAfterSave();
  };

  /**
   * Schritt „done“ quittiert, bevor geschrieben ist (erst nach dem Foto-Upload).
   * Liefert der Aufrufer eine Zusage, zeigt er ihren Stand; sonst ehrlich
   * „Erfasst – wird gespeichert“. Am Speichern selbst aendert das nichts.
   * Eine Ablehnung geht zusaetzlich an den Bug-Report und in den Balken
   * „Nicht gespeichert“ — auch wenn laengst der naechste Eintrag in der Maske steht.
   */
  const speichernVerfolgen = (ergebnis: unknown, bezeichnung: string) => {
      const lauf = ++speicherLaufRef.current;
      if (!istZusage(ergebnis)) {
          setSpeicherStand('erfasst');
          return;
      }
      setSpeicherStand('laeuft');
      setLaufendeSpeicherungen(n => n + 1);
      ergebnis.then(
          () => {
              setLaufendeSpeicherungen(n => Math.max(0, n - 1));
              const stand = speicherStandNach(lauf, speicherLaufRef.current, 'fertig');
              if (stand) setSpeicherStand(stand);
          },
          (err: unknown) => {
              setLaufendeSpeicherungen(n => Math.max(0, n - 1));
              console.error('Eintrag aus dem Assistenten nicht gespeichert', err);
              void import('../../services/bugReportService')
                  .then(({ captureBug }) => captureBug(speicherFehlerBericht(err)))
                  .catch(() => undefined);
              setNichtGespeichert(prev => [...prev, bezeichnung]);
              const stand = speicherStandNach(lauf, speicherLaufRef.current, 'fehler');
              if (stand) setSpeicherStand(stand);
          },
      );
  };

  // Serielle Erfassung: nach dem Speichern nicht schließen, sondern
  // "Nächster Mangel"/"Anderer Eintrag" anbieten — Projekt- und Berichtskontext bleiben.
  // Der Eintrag ist an onSave/onCloseItem uebergeben und damit im Speicherweg:
  // der Entwurf geht sofort — ein gespeicherter Eintrag kommt nie als Entwurf wieder.
  const resetAfterSave = () => {
      entwurfWeg();
      setPhotos([]);
      setFotoLoeschFrage(null);
      setOrtsvorschlag(null);
      setAutoOrt(null);
      setEditingPhotoIndex(null);
      setClearanceTarget(null);
      setData({
          title: '', observation: '', location: '', responsiblePerson: '',
          status: 'open', planId: undefined, planCoordinates: undefined, verortung: undefined,
          deadline: defaultDeadline(), immediateAction: false
      });
      setDeadlineTouched(false);
      setKatalogAuswahl(null);
      setVorbefuellungVerbraucht(true);
      setItemType('defect');
      setStep('done');
  };

  /**
   * Tipp auf den Plan im Verortungsdialog: WEITERE Nadel (Betreiber 28.09.: mehrere Stellen, auch
   * auf mehreren Plaenen). Der Dialog bleibt offen. Eine Sensor-Nadel bleibt samt `verortung`
   * vorn stehen; die Hand-Nadel kommt dahinter und wird nie zur Sensor-Nadel (planPins,
   * ausdrueckliche Markierung). Die Live-Bestaetigung („hier bin ich“)
   * meldet der Dialog selbst ueber onPlanGetippt.
   */
  const handlePinSetzen = (pin: PlanPin) => {
      const pName = plans.find(p => p.id === pin.planId)?.name || 'Plan';
      setData(prev => ({
          ...prev,
          ...pinHinzufuegen(prev, pin),
          location: prev.location || pName, // Auto-fill text if empty
      }));
      // Pin von Hand: der Auto-Zustand endet (sonst loeschte „Rueckgaengig“ den Hand-Pin, Codex-Review 27.09.).
      setAutoOrt(null);
  };

  /** Nadel antippen = entfernen; faellt die Sensor-Nadel weg, auch ihre `verortung`. */
  const handlePinEntfernen = (index: number) => {
      setData(prev => ({ ...prev, ...pinEntfernen(prev, index) }));
      // Auch das ist eine Pin-Aktion von Hand: „Rueckgaengig“ der Automatik gilt nicht mehr.
      setAutoOrt(null);
  };

  const uniqueCompanies = Array.from(new Set(contacts.map(c => c.company).filter(Boolean))).sort();

  /** Freimeldung eines gewaehlten Mangels: Foto + Kommentar, sonst nichts. */
  const isTargetedClearance = itemType === 'clearance' && !!clearanceTarget;
  // Dort wird nur das erste Foto uebernommen (closedImage): Grenze sichtbar 1, weitere Aufnahme gesperrt.
  const maxFotos = fotoGrenze(isTargetedClearance, MAX_PHOTOS);
  const fotosVoll = photos.length >= maxFotos;
  // Chips „wie zuvor“ in „Wer“/„Wo“ (Serienerfassung).
  const wieZuvorFirma = wieZuvor(vorige.firma, data.responsiblePerson);
  const wieZuvorOrt = wieZuvor(vorige.ort, data.location);
  /** Der Schritt, dessen Hauptknopf schreibt (Information und Freimeldung speichern früher). */
  const isSaveStep = step === 'where' || (step === 'what' && (itemType === 'general' || isTargetedClearance));

  // Pflichtfelder je Schritt — dieselbe Regel wie die Desktop-Karte und die
  // Pruefung beim Anlegen (InspectionList): der offene Mangel braucht Titel,
  // Beschreibung, Firma und Ort; Hinweis/Positiv Titel und Beschreibung;
  // die Information Ueberschrift und Text. Die Freimeldung eines gewaehlten
  // Mangels braucht nichts, die bezugslose wie bisher Titel oder Foto.
  // „Weiter" bleibt gesperrt, solange im AKTUELLEN Schritt etwas fehlt —
  // die Karte am Desktop laesst sich sonst nicht mehr abschliessen.
  const titelFehlt = !data.title?.trim();
  const textFehlt = !data.observation?.trim();
  const firmaFehlt = !data.responsiblePerson?.trim();
  const ortFehlt = !data.location?.trim();
  const fehltImSchritt = (): string[] => {
      // Standardmangel: nichts Pflicht — „Freitext“ ueberspringt den Schritt.
      if (step === 'katalog') return [];
      if (step === 'what') {
          if (isTargetedClearance) return [];
          if (itemType === 'clearance') return (titelFehlt && photos.length === 0) ? ['Titel oder Foto'] : [];
          return [...(titelFehlt ? [itemType === 'general' ? 'Überschrift' : 'Titel'] : []), ...(textFehlt ? [itemType === 'general' ? 'Text' : 'Beschreibung'] : [])];
      }
      if (step === 'who') return (itemType === 'defect' && firmaFehlt) ? ['Firma'] : [];
      if (step === 'where') return (itemType === 'defect' && ortFehlt) ? ['Ort'] : [];
      return [];
  };
  const fehlend = fehltImSchritt();
  const pflichtHinweis = (feld: boolean) => feld
      ? <span className="text-[11px] font-bold text-red-500 mt-1 block">Pflichtfeld</span>
      : null;

  /** Ein Schritt zurueck — derselbe Weg fuer den Zurueck-Knopf unten und die Android-Zurueck-Taste. */
  const schrittZurueck = () => {
      if(step === 'finish') setStep('where');
      else if(step === 'where') setStep('who');
      else if(step === 'who') setStep('what');
      else if(step === 'what') {
          // Mangel mit Katalog: zurueck in die Torte (auf der Ebene der Auswahl).
          const ziel = schrittVorDetails({ katalogAktiv: katalogWeg, itemType, gezielteFreimeldung: isTargetedClearance });
          if (ziel === 'katalog') katalogOeffnen();
          else setStep(ziel);
      }
      else if(step === 'pick') setStep('type');
      else if(step === 'katalog') setStep('type');
  };

  // Nichts eingegeben? Dieselbe Pruefung fuer X oben, „Abbrechen“ und die
  // Zurueck-Taste im ersten Schritt (Regel und Felder: wizardLogik.ts). Die
  // Vorbefuellung zaehlt nur, bis sie mit dem ersten Eintrag verbraucht ist.
  const unberuehrt = entwurfUnberuehrt({
      vorbefuellt: !!initialData && !vorbefuellungVerbraucht,
      fotos: photos.length,
      daten: data,
      hatPins: hatPins(data),
      deadlineTouched,
      freimeldeZiel: !!clearanceTarget,
      katalogAuswahl: !!katalogAuswahl,
  });

  // ─── Lokaler Entwurf: sichern, loeschen, anbieten ─────────────────────────
  // Nur, solange der Speicher dieser Sitzung gehoert (Phase 'frei'): waehrend des
  // Lesens beim Oeffnen und waehrend des Angebots wird nichts geschrieben, sonst
  // ersetzte die neue Sitzung den alten Entwurf ungefragt (genau einer je Schluessel).
  const freimeldeZielId = clearanceTarget?.id ?? null;
  const entwurfZustand = useMemo<EntwurfZustand>(() => ({
      typ: itemType, schritt: step, mitKatalog, daten: data, fotos: photos, freimeldeZielId, deadlineTouched, katalogAuswahl,
  }), [itemType, step, mitKatalog, data, photos, freimeldeZielId, deadlineTouched, katalogAuswahl]);
  const entwurfSichern = entwurfAktion({ unberuehrt, schritt: step }) === 'sichern';

  // Fuer den Abschluss des Lesens (nur im Rueckruf gelesen): steht die Maske noch leer im Schritt „Typ“?
  const maskeUnberuehrtRef = useRef(!initialData);
  useEffect(() => { maskeUnberuehrtRef.current = step === 'type' && unberuehrt; });

  // Beim Oeffnen: liegt ein Entwurf? Ohne Vorbefuellung → Leiste; zu alt oder unlesbar → weg.
  useEffect(() => {
      if (!entwurfSchluessel) return;
      let aktiv = true;
      // Nach einem Prozess-Aus kann das erste Oeffnen der Datenbank laenger dauern
      // als die Zeitgrenze — genau dann liegt der zu rettende Entwurf. Deshalb ein
      // zweiter Leseversuch, bevor die Sitzung auf 'gesperrt' geht.
      const lesen = () => entwurfLaden(entwurfSchluessel).then(roh => (roh === ENTWURF_UNLESBAR ? entwurfLaden(entwurfSchluessel) : roh));
      void lesen().then(roh => {
          if (!aktiv) return;
          // Nicht lesbar (Zeitgrenze, Fehler): ob einer liegt, ist unbekannt — dann
          // gehoert der Speicher dieser Sitzung nicht; der Entwurf bleibt unangetastet.
          if (roh === ENTWURF_UNLESBAR) {
              setEntwurfPhase('gesperrt');
              return;
          }
          const jetzt = Date.now();
          const entwurf = entwurfPruefen(roh, jetzt);
          if (roh !== null && !entwurf) void entwurfLoeschen(entwurfSchluessel);
          const phase = phaseNachLaden({ entwurf, vorbefuellt: mitVorbefuellung, maskeUnberuehrt: maskeUnberuehrtRef.current });
          if (phase === 'angebot' && entwurf) setEntwurfAngebot({ entwurf, zeit: entwurfZeitText(entwurf.gespeichertAm, jetzt) });
          setEntwurfPhase(phase);
      }).catch(() => {
          // Unerwartet: den liegenden Entwurf nicht anfassen — er kommt beim naechsten Oeffnen.
          if (aktiv) setEntwurfPhase('gesperrt');
      });
      return () => { aktiv = false; };
  }, [entwurfSchluessel, mitVorbefuellung]);

  // Entprellt sichern; unberuehrt oder uebergeben („done“) → loeschen.
  useEffect(() => {
      if (!entwurfSchluessel || entwurfPhase !== 'frei') return;
      if (!entwurfSichern) { void entwurfLoeschen(entwurfSchluessel); return; }
      const lauf = entwurfLaufRef.current;
      const uhr = window.setTimeout(() => {
          // Inzwischen uebergeben oder verworfen: nichts mehr schreiben.
          if (lauf !== entwurfLaufRef.current) return;
          void entwurfSpeichern(entwurfSchluessel, entwurfAusZustand(entwurfZustand, Date.now()));
      }, ENTWURF_ENTPRELLEN_MS);
      return () => window.clearTimeout(uhr);
  }, [entwurfSchluessel, entwurfPhase, entwurfSichern, entwurfZustand]);

  /** Vor Kamera und Album SOFORT sichern (ohne Entprellen) — dort beendet Android gern die App. */
  const entwurfSofortSichern = () => {
      if (!entwurfSchluessel || entwurfPhase !== 'frei' || !entwurfSichern) return;
      void entwurfSpeichern(entwurfSchluessel, entwurfAusZustand(entwurfZustand, Date.now()));
  };

  /** Entwurf dieser Sitzung loeschen: Eintrag uebergeben, „Verwerfen“, Schliessen ohne Eingabe. */
  const entwurfWeg = () => {
      if (!entwurfSchluessel || entwurfPhase !== 'frei') return;
      entwurfLaufRef.current += 1;
      void entwurfLoeschen(entwurfSchluessel);
  };

  /**
   * Leiste „Fortsetzen“: Typ, Schritt, Felder, Fotos, Frist-Merker und
   * Standardmangel zurueck. Den sicheren Schritt und das Freimelde-Ziel (per id
   * aus den offenen Maengeln) waehlt entwurfWiederherstellen.
   */
  const entwurfFortsetzen = () => {
      if (!entwurfAngebot) return;
      const w = entwurfWiederherstellen(entwurfAngebot.entwurf, {
          offeneMaengel: openItems,
          freimeldungMitAuswahl: openItems.length > 0 && !!onCloseItem,
      });
      setItemType(w.typ);
      setMitKatalog(w.mitKatalog);
      setData(w.daten);
      setPhotos(w.fotos);
      setDeadlineTouched(w.deadlineTouched);
      setKatalogAuswahl(w.katalogAuswahl);
      setClearanceTarget(w.freimeldeZiel);
      setEntwurfAngebot(null);
      setEntwurfPhase('frei');
      setStep(w.schritt);
  };

  /** Leiste „Verwerfen“: der alte Entwurf geht, der Assistent beginnt leer. */
  const entwurfAngebotVerwerfen = () => {
      if (entwurfSchluessel) {
          entwurfLaufRef.current += 1;
          void entwurfLoeschen(entwurfSchluessel);
      }
      setEntwurfAngebot(null);
      setEntwurfPhase('frei');
  };

  /**
   * Schliessen — erst, wenn nichts mehr gespeichert wird (sonst danach von
   * selbst). Scheitert waehrend des Wartens etwas, bleibt der Assistent offen:
   * der rote Balken „Nicht gespeichert“ ginge sonst mit ihm.
   */
  const schliessen = () => {
      if (laufendeSpeicherungen > 0) { setSchliessenGewuenscht(true); return; }
      onCancel();
  };
  const weiterErfassen = () => {
      setSchliessenGewuenscht(false);
      setNotausgang(false);
  };
  useEffect(() => {
      if (schliessenGewuenscht && darfSchliessen(laufendeSpeicherungen, nichtGespeichert.length)) onCancel();
  }, [schliessenGewuenscht, laufendeSpeicherungen, nichtGespeichert.length, onCancel]);
  useEffect(() => {
      if (!schliessenGewuenscht) return;
      const uhr = window.setTimeout(() => setNotausgang(true), SCHLIESSEN_NOTAUSGANG_MS);
      return () => window.clearTimeout(uhr);
  }, [schliessenGewuenscht]);

  /**
   * Leer → schliessen wie bisher (ein Entwurf dieser Sitzung geht mit — ein
   * angebotener oder fremder bleibt liegen); sonst die Leiste „Eintrag verwerfen?“.
   */
  const schliessenWollen = () => {
      if (schliessenAktion({ schritt: step, unberuehrt }) === 'schliessen') { entwurfWeg(); schliessen(); }
      else setVerwerfenFrage(true);
  };

  // Zurueck-Taste der App und Zurueck-Geste im Handy-Browser (appUmfang.registriereZurueck):
  // der Assistent ist eine Vollbildflaeche und bekommt den Druck selbst — so
  // wie sein eigener Zurueck-Knopf, nie auf Kosten einer Eingabe.
  const zurueckTasteRef = useRef<() => void>(() => {});
  // Wurzel des Assistenten (der Verortungsdialog liegt darin): ein
  // fremder Dialog darueber hat bei der Zurueck-Taste Vorrang (appUmfang).
  const flaecheRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
      zurueckTasteRef.current = () => {
          // Bildbearbeitung offen: Striche/Zuschnitt nicht verwerfen.
          if (editingPhotoIndex !== null) return;
          // Verortungsdialog: wie sein „Fertig“ (Nadeln gelten schon).
          if (showPlanPicker) { setShowPlanPicker(false); return; }
          // Rueckfrage „Eintrag verwerfen?“ offen: Zurueck heisst „Weiter bearbeiten“.
          if (verwerfenFrage) { setVerwerfenFrage(false); return; }
          // Wartet auf das Speichern: Zurueck heisst „Weiter erfassen“.
          if (schliessenGewuenscht) { weiterErfassen(); return; }
          // „Fertig": schliessen, sobald alles gespeichert ist.
          if (step === 'done') { schliessen(); return; }
          // Standardmangel: Ebene fuer Ebene zurueck (zuerst eine offene ⓘ), in der Wurzel zu „Typ“.
          if (step === 'katalog') {
              if (!katalogSteuerungRef.current?.zurueck()) setStep('type');
              return;
          }
          if (step === 'type') {
              // Leer: schliessen. Sonst dieselbe Leiste wie X und „Abbrechen“ —
              // frueher blieb der Assistent hier wortlos stehen.
              schliessenWollen();
              return;
          }
          // Weiter hinten: schrittweise zurueck, nie auf Kosten einer Eingabe.
          schrittZurueck();
      };
  });
  useEffect(() => registriereZurueck(() => zurueckTasteRef.current(), () => flaecheRef.current), []);

  // Zeile ueber den Geschoss-Knoepfen im Schritt „Wo“.
  const ortLiveText = !live.aktiv
      ? 'Geschoss wählen'
      : !live.schaetzung
          ? 'Wo sind Sie? Einmal je Begehung antippen'
          : live.schaetzung.zwischen
              ? `Live: zwischen ${live.schaetzung.zwischen[0].name} und ${live.schaetzung.zwischen[1].name}`
              : `Live: vermutlich ${live.schaetzung.ebene?.name ?? '?'}${live.schnappschuss?.position ? ` · GPS ±${Math.round(live.schnappschuss.position.genauigkeitM)} m` : ''}`;

  // Verortungsdialog (docs/planverortung-vertrag.md): Plaene des Abschnitts plus jeder Plan, auf
  // dem der Eintrag schon Nadeln traegt; ohne Nadel startet er auf dem Plan des geschaetzten Geschosses.
  const eigenePlanIds = planIdsVon(data);
  const dialogPlaene = sectionId
      ? plans.filter(p => !p.sectionId || p.sectionId === sectionId || eigenePlanIds.includes(p.id))
      : plans;
  const planAnlegen = onPlanAnlegen && projectId ? { projectId, sectionId, onAngelegt: onPlanAnlegen } : undefined;
  const pinAnzahl = pinsVon(data).length;

  return (
    <div ref={flaecheRef} data-glass-view="mobil" className="fixed inset-0 z-[100] bg-slate-50 flex flex-col animate-in slide-in-from-bottom-10 duration-300">
        
        {/* Header */}
        <div className="bg-white p-4 shadow-sm flex justify-between items-center shrink-0">
            <button type="button" onClick={schliessenWollen} aria-label="Schließen" className="p-2.5 text-slate-400 hover:text-slate-600"><X className="w-6 h-6"/></button>
            <span className="font-bold text-slate-700 text-sm">
                {step === 'type' ? 'Neuer Eintrag' : step === 'katalog' ? 'Standardmangel' : step === 'pick' ? 'Mangel wählen' : step === 'what' ? (isTargetedClearance ? 'Freimeldung' : 'Details') : step === 'who' ? 'Zuständigkeit' : step === 'done' ? speicherText(speicherStand).kopf : 'Abschluss'}
            </span>
            <div className="w-11"></div>
        </div>

        {/*
          Sperrbalken. Bewusst nicht wegklickbar und ganz oben: er ist der
          einzige Hinweis darauf, dass die Maske zwar noch da ist, ihr Inhalt
          aber nirgends mehr hingeht.
        */}
        {saveBlockedNotice && (
            <div role="alert" className="bg-red-600 text-white px-4 py-3 flex items-start gap-3 shrink-0">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <p className="text-sm leading-snug">{saveBlockedNotice}</p>
            </div>
        )}

        {/* Gescheitertes Speichern — steht, bis er weggetippt wird, auch wenn
            laengst der naechste Eintrag in der Maske ist. */}
        {nichtGespeichert.length > 0 && (
            <div role="alert" className="bg-red-600 text-white pl-4 pr-1 py-1 flex items-center gap-3 shrink-0">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <p className="min-w-0 flex-1 text-sm leading-snug py-2">{nichtGespeichertText(nichtGespeichert)}</p>
                <button type="button" onClick={() => { setNichtGespeichert([]); weiterErfassen(); }} className="min-h-11 shrink-0 rounded-xl px-4 text-sm font-semibold active:bg-red-700">OK</button>
            </div>
        )}

        {/*
          STEP 1a: STANDARDMANGEL — die Torte randlos (nicht im gepolsterten
          Inhaltsbereich); sie hat eine eigene untere Leiste mit safe-area,
          die Fussleiste des Assistenten entfaellt in diesem Schritt.
        */}
        {step === 'katalog' && (
            <div className="flex-1 min-h-0 flex flex-col">
                <MangelKatalogAuswahl
                    key={katalogStart.lauf}
                    darstellung="vollbild"
                    steuerungRef={katalogSteuerungRef}
                    startPfad={katalogStart.pfad}
                    onAuswahl={katalogGewaehlt}
                    onVerlassen={() => setStep('type')}
                    onFreitext={() => { katalogLoesen(); setStep('what'); }}
                    sprache={erfassungsSprache}
                />
            </div>
        )}

        {step !== 'katalog' && (
        // Waehrend die Leiste „Nicht gespeicherter Eintrag“ steht: erst entscheiden — die Kacheln sind inert.
        <div inert={!!entwurfAngebot} className={`flex-1 overflow-y-auto p-6 flex flex-col pb-20${entwurfAngebot ? ' opacity-50' : ''}`}>

            {/* STEP 1: TYPE */}
            {step === 'type' && (
                <div className="space-y-4 pt-4">
                    {katalogAktiv ? (
                        /* Mangel mit Wahl des Wegs: Standardmangel (Torte) oder eigener Text mit KI-Vorschlag. */
                        <div className="w-full bg-white p-4 rounded-2xl border-2 border-red-100 shadow-sm space-y-3">
                            <div className="flex items-center gap-4">
                                <div className="bg-red-100 p-3 rounded-full text-red-600"><AlertTriangle className="w-6 h-6" /></div>
                                <div className="text-left"><h3 className="font-bold text-slate-800 text-lg">Mangel</h3><p className="text-slate-400 text-sm">Problem oder Gefahr</p></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button type="button" onClick={() => handleTypeSelect('defect', true)} className="min-h-[56px] rounded-xl border border-red-200 bg-red-50 px-3 py-2 flex items-center gap-2 text-left active:bg-red-100 transition-colors">
                                    <ChartPie className="w-5 h-5 shrink-0 text-red-600" aria-hidden="true" />
                                    {/* Weiches Trennzeichen: auf 360-dp-Handys passt „Standardmangel“ nicht in die halbe Spalte und lief über den Rand. */}
                                    <span className="min-w-0"><span className="block font-bold text-slate-800 text-sm">{'Standard­mangel'}</span><span className="block text-slate-500 text-[11px]">aus dem Katalog</span></span>
                                </button>
                                <button type="button" onClick={() => handleTypeSelect('defect', false)} className="min-h-[56px] rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 flex items-center gap-2 text-left active:bg-violet-100 transition-colors">
                                    <Sparkles className="w-5 h-5 shrink-0 text-violet-600" aria-hidden="true" />
                                    <span className="min-w-0"><span className="block font-bold text-slate-800 text-sm">Eigener Text</span><span className="block text-slate-500 text-[11px]">mit KI-Vorschlag</span></span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => handleTypeSelect('defect')} className="w-full bg-white p-5 rounded-2xl border-2 border-red-100 shadow-sm flex items-center gap-4 active:bg-red-50 transition-colors">
                            <div className="bg-red-100 p-3 rounded-full text-red-600"><AlertTriangle className="w-6 h-6" /></div>
                            <div className="text-left"><h3 className="font-bold text-slate-800 text-lg">Mangel</h3><p className="text-slate-400 text-sm">Problem oder Gefahr</p></div>
                        </button>
                    )}
                    
                    {/*
                      Freimeldung ist kein Eintragstyp, sondern ein Abschluss-Vorgang an einem
                      bestehenden Eintrag — deshalb als einzige Kachel gefüllt statt weiß.
                      Das trennt sie zugleich vom emerald-farbenen "Positiv": zwei ähnliche
                      Grüntöne sind auf dem Baustellen-Handy bei Sonne nicht unterscheidbar.
                      Grünton green-700 statt green-600, weil weißer Text erst dort den
                      Kontrastwert 4.5:1 erreicht (green-600: 3.3:1, zu wenig für den Untertitel).
                    */}
                    <button onClick={() => handleTypeSelect('clearance')} className="w-full bg-green-700 p-5 rounded-2xl border-2 border-green-700 shadow-sm flex items-center gap-4 active:bg-green-800 active:border-green-800 transition-colors">
                        <div className="bg-white/20 p-3 rounded-full text-white"><CheckCircle2 className="w-6 h-6" /></div>
                        <div className="text-left"><h3 className="font-bold text-white text-lg">Freimeldung</h3><p className="text-green-50 text-sm">Sofort erledigt</p></div>
                    </button>

                    <button onClick={() => handleTypeSelect('info')} className="w-full bg-white p-5 rounded-2xl border-2 border-amber-100 shadow-sm flex items-center gap-4 active:bg-amber-50 transition-colors">
                        <div className="bg-amber-100 p-3 rounded-full text-amber-600"><Info className="w-6 h-6" /></div>
                        <div className="text-left"><h3 className="font-bold text-slate-800 text-lg">Hinweis</h3><p className="text-slate-400 text-sm">Beobachtung am Objekt — mit Ort und Firma, kein Mangel</p></div>
                    </button>

                    <button onClick={() => handleTypeSelect('good')} className="w-full bg-white p-5 rounded-2xl border-2 border-emerald-200 shadow-sm flex items-center gap-4 active:bg-emerald-50 transition-colors">
                        <div className="bg-emerald-100 p-3 rounded-full text-emerald-600"><ThumbsUp className="w-6 h-6" /></div>
                        <div className="text-left"><h3 className="font-bold text-slate-800 text-lg">Positiv</h3><p className="text-slate-400 text-sm">Gut gelöst — nichts zu tun</p></div>
                    </button>

                    <button onClick={() => handleTypeSelect('general')} className="w-full bg-white p-5 rounded-2xl border-2 border-blue-100 shadow-sm flex items-center gap-4 active:bg-blue-50 transition-colors">
                        <div className="bg-blue-100 p-3 rounded-full text-blue-600"><FileText className="w-6 h-6" /></div>
                        <div className="text-left"><h3 className="font-bold text-slate-800 text-lg">Information</h3><p className="text-slate-400 text-sm">Steht im Bericht ganz oben — ohne Ort und Firma</p></div>
                    </button>
                </div>
            )}

            {/* STEP 1b: PICK — offenen Mangel fuer die Freimeldung waehlen */}
            {step === 'pick' && (
                <div className="space-y-3">
                    <p className="text-xs font-bold text-slate-400 uppercase">Welcher Mangel ist erledigt?</p>
                    {openItems.map(item => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => handlePickTarget(item)}
                            className="w-full bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-left active:bg-green-50 transition-colors flex gap-3 items-start"
                        >
                            <span className="shrink-0 bg-red-100 text-red-700 font-bold text-xs px-2 py-1 rounded-lg">
                                {item.ticketNumber ? `#${item.ticketNumber}` : 'Mangel'}
                            </span>
                            <span className="min-w-0">
                                <span className="block font-bold text-slate-800 truncate">{item.title || 'Ohne Titel'}</span>
                                <span className="block text-xs text-slate-500 truncate">
                                    {[item.location, item.responsiblePerson].filter(Boolean).join(' · ') || 'Ort/Firma nicht erfasst'}
                                </span>
                            </span>
                        </button>
                    ))}
                    {/* Alter Weg bleibt: Erledigung ohne Bezug zu einem gelisteten Mangel. */}
                    <button
                        type="button"
                        onClick={() => { setClearanceTarget(null); setStep('what'); }}
                        className="w-full p-4 rounded-2xl border-2 border-dashed border-slate-300 text-slate-500 font-bold text-sm active:bg-slate-100"
                    >
                        Ohne Bezug zu einem Mangel erfassen
                    </button>
                </div>
            )}

            {/* STEP 2: WHAT (Photo & Text) */}
            {step === 'what' && (
                <div className="space-y-6">
                    {/* Dual Photo Buttons */}
                    <div className="grid grid-cols-2 gap-4">
                         <button onClick={kameraOeffnen} disabled={fotosVoll} className="bg-blue-600 text-white p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-lg active:scale-95 transition-transform disabled:opacity-50">
                             <Camera className="w-8 h-8" />
                             <span className="font-bold text-xs">Kamera</span>
                         </button>
                         {/* Vor dem Album sofort sichern (wie vor der Kamera) — auch dort kann Android die App beenden. */}
                         <button onClick={() => { entwurfSofortSichern(); galleryInputRef.current?.click(); }} disabled={fotosVoll} className="bg-white text-slate-600 border border-slate-200 p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm active:scale-95 transition-transform disabled:opacity-50">
                             <ImageIcon className="w-8 h-8 text-slate-400" />
                             <span className="font-bold text-xs">Album</span>
                         </button>
                         <input type="file" accept="image/*" capture="environment" className="hidden" ref={cameraInputRef} onChange={handlePhotoUpload} />
                         <input type="file" accept={`image/*,${HEIC_ACCEPT}`} multiple className="hidden" ref={galleryInputRef} onChange={handlePhotoUpload} />
                         {/* Wechsel auf die eingebaute Kamera (Einblendung + Ortsvorschlag) — nur wenn die Verortung nutzbar ist. */}
                         {kameraMitVerortung && (
                             <button type="button" onClick={appKameraOeffnen} disabled={fotosVoll} className="col-span-2 min-h-[44px] bg-white text-blue-700 border border-blue-200 px-4 py-3 rounded-2xl flex items-center justify-center gap-2 shadow-sm active:scale-95 transition-transform disabled:opacity-50">
                                 <Crosshair className="w-5 h-5" />
                                 <span className="font-bold text-xs">App-Kamera mit Ort-Einblendung</span>
                             </button>
                         )}
                    </div>

                    {/* Bis zu drei Bilder, jedes bearbeitbar (ImageEditor wie am
                        Desktop: Pfeile, Kreise, Zuschnitt) oder loeschbar. Die
                        Knoepfe untereinander, damit jeder 44 px hoch und die
                        deutsche Beschriftung breit genug ist (auf sehr schmalen
                        Handys rutscht sie unter das Symbol); Loeschen in zwei Tipps. */}
                    {photos.length > 0 && (
                        <div className="grid grid-cols-3 gap-3">
                            {photos.map((p, idx) => (
                                <div key={idx} className="flex flex-col rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                                    <div className="relative aspect-[3/4] bg-black">
                                        <img src={p} alt="" className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex flex-col border-t border-slate-100">
                                        <button type="button" onClick={() => { setFotoLoeschFrage(null); setEditingPhotoIndex(idx); }} className="min-h-11 px-1 text-blue-600 active:bg-blue-50 flex flex-wrap items-center justify-center gap-x-1 border-b border-slate-100" title="Foto bearbeiten">
                                            <Edit2 className="w-3.5 h-3.5 shrink-0" /><span className="text-[11px] font-bold leading-tight">Bearbeiten</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => fotoLoeschenTippen(idx)}
                                            className={`min-h-11 px-1 flex flex-wrap items-center justify-center gap-x-1 ${fotoLoeschFrage === idx ? 'bg-red-600 text-white' : 'text-red-600 active:bg-red-50'}`}
                                            title="Foto löschen"
                                        >
                                            <Trash2 className="w-3.5 h-3.5 shrink-0" /><span className="text-[11px] font-bold leading-tight text-center">{fotoLoeschFrage === idx ? 'Wirklich löschen?' : 'Löschen'}</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-[11px] text-slate-400 -mt-3">{fotoZaehlerText(photos.length, maxFotos, isTargetedClearance)}</p>

                    <div className="space-y-4">
                        {/* Standardmangel: Chip mit [Ändern]/[×]; gelber Hinweis,
                            solange eine Luecke im Text steht. */}
                        {itemType === 'defect' && katalogAuswahl && (
                            <KatalogChip
                                pfad={katalogAuswahl.pfad}
                                pikto={katalogAuswahl.pikto}
                                platzhalter={angabeFehlt(data)}
                                art={katalogAuswahl.art}
                                anzahl={katalogAuswahl.anzahl}
                                onAendern={katalogOeffnen}
                                onEntfernen={katalogLoesen}
                                sprache={erfassungsSprache}
                            />
                        )}
                        {isTargetedClearance && clearanceTarget ? (
                            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                                <p className="text-[11px] font-bold text-green-700 uppercase mb-1">Wird freigemeldet</p>
                                <p className="font-bold text-slate-800">
                                    {clearanceTarget.ticketNumber ? `#${clearanceTarget.ticketNumber} ` : ''}{clearanceTarget.title}
                                </p>
                                <p className="text-xs text-slate-500">
                                    {[clearanceTarget.location, clearanceTarget.responsiblePerson].filter(Boolean).join(' · ')}
                                </p>
                            </div>
                        ) : (
                        <input 
                            value={data.title}
                            onChange={(e) => { if (isSuggesting) kiAbbrechen(); setKiVorher(null); setData({...data, title: e.target.value}); }}
                            className="w-full p-4 text-lg font-bold border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder={itemType === 'clearance' ? "Was wurde erledigt?" : itemType === 'general' ? "Überschrift" : "Stichwort, z.B. Keine Schutzbrille beim Flexen"}
                        />
                        )}
                        {!isTargetedClearance && itemType !== 'clearance' && pflichtHinweis(titelFehlt)}
                        {/* Ein Knopf statt Fenster: das Stichwort im Titel wird zu
                            Titel, Bemerkung und Vorschrift ausformuliert — beim Mangel
                            und beim Hinweis (der Proxy formuliert einen Mängeltext).
                            Ohne Netz gesperrt; waehrend des Laufs „Abbrechen“ darunter
                            (nicht auf dem Knopf selbst: ein nachgetippter Tipp braeche
                            sonst gleich wieder ab); nach der Übernahme ein
                            einstufiges „Rückgängig“ an derselben Stelle. */}
                        {itemType !== 'clearance' && itemType !== 'general' && (
                            <>
                                {kiFuerTyp(itemType) && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleSuggestText}
                                            disabled={isSuggesting || !netzDa || !data.title?.trim()}
                                            className="w-full py-3 rounded-xl bg-violet-600 text-white font-bold flex items-center justify-center gap-2 shadow-sm active:scale-95 transition-transform disabled:opacity-50"
                                        >
                                            {isSuggesting ? <Loader2 className="w-5 h-5 animate-spin" /> : !netzDa ? <WifiOff className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
                                            {isSuggesting ? 'Text wird formuliert…' : !netzDa ? 'KI braucht Netz' : 'Text aus Stichwort vorschlagen'}
                                        </button>
                                        {isSuggesting && (
                                            <div className="flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 pl-3">
                                                <p className="min-w-0 flex-1 text-sm font-medium text-violet-700">Selbst schreiben bricht den Vorschlag ab</p>
                                                <button
                                                    type="button"
                                                    onClick={kiAbbrechen}
                                                    className="min-h-11 shrink-0 rounded-xl px-3 flex items-center gap-1.5 text-sm font-semibold text-violet-700 active:bg-violet-100"
                                                >
                                                    <X className="w-4 h-4" /> Abbrechen
                                                </button>
                                            </div>
                                        )}
                                        {suggestError && (
                                            <p className="text-sm text-red-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />{suggestError}</p>
                                        )}
                                        {kiVorher && !isSuggesting && (
                                            <div className="flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 pl-3">
                                                <p className="min-w-0 flex-1 text-sm font-medium text-violet-700">KI-Vorschlag übernommen</p>
                                                <button
                                                    type="button"
                                                    onClick={kiRueckgaengig}
                                                    className="min-h-11 shrink-0 rounded-xl px-3 flex items-center gap-1.5 text-sm font-semibold text-violet-700 active:bg-violet-100"
                                                >
                                                    <Undo2 className="w-4 h-4" /> Rückgängig
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                                {data.regulation && (
                                    <p className="text-xs text-slate-500">Vorschrift: <span className="font-mono font-semibold text-indigo-700">{data.regulation}</span></p>
                                )}
                            </>
                        )}
                        <textarea 
                            value={data.observation}
                            onChange={(e) => { if (isSuggesting) kiAbbrechen(); setKiVorher(null); setData({...data, observation: e.target.value}); }}
                            rows={3}
                            className="w-full p-4 text-base border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                            placeholder={isTargetedClearance ? "Kommentar zur Erledigung (optional)..." : itemType === 'general' ? "Text zur Information..." : "Beschreibung..."}
                        />
                        {!isTargetedClearance && itemType !== 'clearance' && pflichtHinweis(textFehlt)}
                    </div>
                </div>
            )}

            {/* STEP 3: WHO */}
            {step === 'who' && (
                <div className="space-y-4">
                     <label className="text-xs font-bold text-slate-400 uppercase">Zuständige Firma{itemType === 'defect' ? ' *' : ''}</label>
                     {/* Serienerfassung: Firma des vorigen Eintrags als Angebot, nicht
                         vorbelegt — ein Tipp übernimmt und geht weiter wie die Firmenknöpfe. */}
                     {wieZuvorFirma && (
                         <button
                            type="button"
                            onClick={() => { setData({...data, responsiblePerson: wieZuvorFirma}); zuOrtSchritt(); }}
                            className="max-w-full min-h-11 px-4 rounded-full border-2 border-blue-200 bg-white text-blue-700 text-sm font-semibold flex items-center gap-2 active:scale-95 transition-all"
                         >
                             <History className="w-4 h-4 shrink-0" /><span className="truncate">wie zuvor: {wieZuvorFirma}</span>
                         </button>
                     )}
                     {/* „Alle": betrifft jede Firma auf der Baustelle — ein Tipp
                         statt tippen. Gespeichert wird der Text wie jede Firma. */}
                     <button
                        type="button"
                        onClick={() => { setData({...data, responsiblePerson: ALLE_FIRMEN}); zuOrtSchritt(); }}
                        className={`w-full p-4 rounded-xl border-2 font-bold flex items-center justify-center gap-2 active:scale-95 transition-all ${data.responsiblePerson === ALLE_FIRMEN ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200'}`}
                     >
                         <Users className="w-5 h-5" /> Alle Firmen
                     </button>
                     <div className="grid grid-cols-2 gap-3">
                         {uniqueCompanies.map(c => (
                             <button 
                                key={c}
                                onClick={() => { setData({...data, responsiblePerson: c}); zuOrtSchritt(); }}
                                className={`p-3 rounded-xl border text-sm font-medium text-left active:scale-95 transition-all ${data.responsiblePerson === c ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}
                             >
                                 {c}
                             </button>
                         ))}
                     </div>
                     <input 
                        value={data.responsiblePerson}
                        onChange={(e) => setData({...data, responsiblePerson: e.target.value})}
                        className="w-full p-4 border border-slate-200 rounded-xl mt-4"
                        placeholder="Oder Name eingeben..."
                     />
                     {itemType === 'defect' && pflichtHinweis(firmaFehlt)}
                </div>
            )}

            {/* STEP 4: WHERE */}
            {step === 'where' && (
                <div className="space-y-6">
                    <OrtAutomatik
                        diagnose={diagnose}
                        auto={autoAktiv && autoOrt ? { text: autoOrt.text, quellen: autoOrt.quellen } : null}
                        onRueckgaengig={autoRueckgaengig}
                        geschosse={geschossEbenen(projektVerortung.geschosse)}
                        lagen={geruestLagen(projektVerortung.geruest)}
                        geschaetztId={live.schaetzung && !live.schaetzung.zwischen ? live.schaetzung.ebene?.id ?? null : null}
                        liveText={ortLiveText}
                        onEbene={ebeneTippen}
                    />
                    {ortsvorschlag && (
                        <OrtsvorschlagKarte
                            vorschlag={ortsvorschlag.vorschlag}
                            plan={plans.find(p => p.id === ortsvorschlag.vorschlag.planId) ?? null}
                            onUebernehmen={() => ortsvorschlagUebernehmen(false)}
                            onAufPlan={plans.length > 0 ? () => ortsvorschlagUebernehmen(true) : undefined}
                            onVerwerfen={() => setOrtsvorschlag(null)}
                        />
                    )}
                    {/* Auch ohne Plan, wenn einer hochgeladen werden kann (Dialog mit „Plan hochladen“). */}
                    {(plans.length > 0 || planAnlegen) && (
                        <button
                            onClick={() => setShowPlanPicker(true)}
                            className={`w-full p-4 rounded-xl border-2 border-dashed flex items-center justify-center gap-3 active:bg-blue-50 transition-colors ${pinAnzahl > 0 ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-300 text-slate-500'}`}
                        >
                            <MapIcon className="w-6 h-6" />
                            <span className="font-bold">{pinAnzahl > 0 ? `Auf Plan markiert (${pinAnzahl}) – ändern` : 'Auf Plan markieren'}</span>
                        </button>
                    )}
                    
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Ort / Bereich (Text){itemType === 'defect' ? ' *' : ''}</label>
                        {/* Serienerfassung: Ort des vorigen Eintrags als Angebot — ein Tipp trägt ihn ein (wie Tippen). */}
                        {wieZuvorOrt && (
                            <button
                                type="button"
                                onClick={() => setData({...data, location: wieZuvorOrt})}
                                className="mb-3 max-w-full min-h-11 px-4 rounded-full border-2 border-blue-200 bg-white text-blue-700 text-sm font-semibold flex items-center gap-2 active:scale-95 transition-all"
                            >
                                <History className="w-4 h-4 shrink-0" /><span className="truncate">wie zuvor: {wieZuvorOrt}</span>
                            </button>
                        )}
                        <input 
                            value={data.location}
                            onChange={(e) => setData({...data, location: e.target.value})}
                            className="w-full p-4 text-base border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="z.B. EG, Treppenhaus"
                        />
                        {itemType === 'defect' && pflichtHinweis(ortFehlt)}
                    </div>

                    {itemType === 'defect' && (
                        <div>
                             <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Frist</label>
                             {data.immediateAction ? (
                                 /* Sofortmaßnahme: Verantwortliche vor Ort informiert, keine Frist. */
                                 <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3">
                                     <Zap className="w-6 h-6 text-amber-600 shrink-0" />
                                     <div className="flex-1">
                                         <p className="font-bold text-amber-800">Sofortmaßnahme</p>
                                         <p className="text-xs text-amber-700">Verantwortliche wurden informiert – keine Frist.</p>
                                     </div>
                                     <button
                                        type="button"
                                        onClick={() => setData({...data, immediateAction: false, deadline: deadlineTouched ? data.deadline : defaultDeadline()})}
                                        className="text-xs font-bold text-amber-800 underline shrink-0"
                                     >
                                         Frist setzen
                                     </button>
                                 </div>
                             ) : (
                                 <>
                                     <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-slate-200">
                                         <Calendar className="w-5 h-5 text-slate-400" />
                                         <input
                                            type="date"
                                            value={data.deadline}
                                            onChange={(e) => { setDeadlineTouched(true); setData({...data, deadline: e.target.value}); }}
                                            className="flex-1 bg-transparent outline-none text-slate-700 font-bold"
                                         />
                                     </div>
                                     <button
                                        type="button"
                                        onClick={() => setData({...data, immediateAction: true, deadline: ''})}
                                        className="mt-3 w-full py-3 rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-800 font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                                     >
                                         <Zap className="w-5 h-5" /> Sofortmaßnahme – Verantwortliche informiert
                                     </button>
                                 </>
                             )}
                        </div>
                    )}
                </div>
            )}

            {/* STEP: DONE */}
            {/* „Gespeichert“ erst, wenn der Aufrufer es bestätigt — vorher „Erfasst – wird gespeichert“. */}
            {step === 'done' && (
                <div className="flex-1 flex flex-col items-center justify-center gap-5 pt-8">
                    {speicherStand === 'fehler' ? (
                        <div className="bg-red-100 p-5 rounded-full text-red-600"><AlertTriangle className="w-12 h-12" /></div>
                    ) : speicherStand === 'laeuft' ? (
                        <div className="bg-blue-50 p-5 rounded-full text-blue-600"><Loader2 className="w-12 h-12 animate-spin" /></div>
                    ) : (
                        <div className="bg-emerald-100 p-5 rounded-full text-emerald-600"><CheckCircle2 className="w-12 h-12" /></div>
                    )}
                    <p role="status" className={`font-bold text-lg text-center ${speicherStand === 'fehler' ? 'text-red-700' : 'text-slate-700'}`}>{speicherText(speicherStand).text}</p>
                </div>
            )}

        </div>
        )}

        {/* Footer Nav — nicht im Schritt „Standardmangel“ (die Torte hat ihre eigene Leiste) */}
        {step !== 'katalog' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-4 border-t border-slate-200 grid grid-cols-2 gap-4 pb-safe">
            {step === 'type' ? (
                 <button onClick={schliessenWollen} className="col-span-2 py-4 bg-slate-100 rounded-2xl font-bold text-slate-600">Abbrechen</button>
            ) : step === 'done' ? (
                <>
                    {/* Serienerfassung: „Nächster Mangel“ ohne Typwahl — auf dem Weg, der
                        zuletzt beim Mangel gewählt war (Standardmangel → Torte, eigener
                        Text → „Details“); „Anderer Eintrag“ zur Typwahl. */}
                    <button type="button" onClick={() => setStep('type')} className="col-span-2 py-3 rounded-2xl border-2 border-blue-200 bg-white text-blue-700 font-semibold">Anderer Eintrag</button>
                    <button onClick={schliessen} className="py-4 bg-slate-100 rounded-2xl font-bold text-slate-600">Fertig</button>
                    <button type="button" onClick={() => handleTypeSelect('defect', mitKatalog)} className="py-4 bg-blue-600 text-white rounded-2xl font-bold shadow-lg">Nächster Mangel</button>
                </>
            ) : (
                <>
                    <button 
                        onClick={schrittZurueck}
                        className="py-4 bg-slate-100 rounded-2xl font-bold text-slate-600"
                    >
                        Zurück
                    </button>
                    
                    <button
                        onClick={() => {
                            // Information hat weder Zuständigkeit noch Ort — direkt speichern;
                            // die Freimeldung eines gewaehlten Mangels ebenso.
                            if (step === 'what' && (itemType === 'general' || isTargetedClearance)) handleSave();
                            else if (step === 'what') setStep('who');
                            else if (step === 'who') zuOrtSchritt();
                            else if (step === 'where') handleSave();
                        }}
                        // Gesperrt wird nur der Schritt, der tatsaechlich
                        // schreibt: durch die Maske blaettern (und die eigene
                        // Eingabe herausschreiben) muss weiter moeglich sein.
                        disabled={(saveBlockedNotice ? isSaveStep : false)
                            || step === 'pick'
                            || fehlend.length > 0}
                        className="py-4 bg-blue-600 text-white rounded-2xl font-bold shadow-lg disabled:opacity-50"
                    >
                        {isSaveStep ? 'Speichern' : 'Weiter'}
                    </button>
                    {fehlend.length > 0 && step !== 'pick' && (
                        <p className="col-span-2 -mt-2 text-[11px] text-red-600 text-center">Fehlt noch: {fehlend.join(', ')}</p>
                    )}
                </>
            )}
        </div>
        )}

        {/* Rueckfrage beim Schliessen eines angefangenen Eintrags — als Leiste ueber
            der Fussleiste (auch ueber der Leiste der Torte). Der sichere Weg
            „Weiter bearbeiten“ liegt unten ueber die ganze Breite — genau dort,
            wo ein nachgetipptes „Zurück“/„Abbrechen“ landet; „Verwerfen“ steht
            eine Zeile darueber und braucht einen eigenen, gezielten Tipp. */}
        {verwerfenFrage && (
            <div role="alertdialog" aria-label="Eintrag verwerfen?" className="fixed bottom-0 left-0 right-0 z-10 bg-white p-4 border-t border-slate-200 shadow-lg pb-safe space-y-3">
                <p className="font-bold text-slate-800 text-center">Eintrag verwerfen?</p>
                <div className="grid gap-3">
                    <button type="button" onClick={() => { entwurfWeg(); setVerwerfenFrage(false); schliessen(); }} className="py-3 bg-slate-100 rounded-2xl font-semibold text-red-700">Verwerfen</button>
                    <button type="button" onClick={() => setVerwerfenFrage(false)} className="py-4 bg-blue-600 text-white rounded-2xl font-semibold shadow-lg">Weiter bearbeiten</button>
                </div>
            </div>
        )}

        {/* Lokaler Entwurf beim Oeffnen (nur ohne Vorbefuellung): gleiche Leiste wie
            „Eintrag verwerfen?“. Der sichere Weg „Fortsetzen“ unten ueber die ganze
            Breite; „Verwerfen“ eine Zeile darueber, mit eigenem, gezieltem Tipp.
            X, „Abbrechen“ und die Zurueck-Taste schliessen — der Entwurf bleibt liegen. */}
        {entwurfAngebot && (
            <div role="alertdialog" aria-label="Nicht gespeicherter Eintrag" className="fixed bottom-0 left-0 right-0 z-10 bg-white p-4 border-t border-slate-200 shadow-lg pb-safe space-y-3">
                <div className="text-center">
                    <p className="font-bold text-slate-800">Nicht gespeicherter Eintrag von {entwurfAngebot.zeit}</p>
                    <p className="text-xs text-slate-500">{entwurfBeschreibung(entwurfAngebot.entwurf)}</p>
                </div>
                <div className="grid gap-3">
                    <button type="button" onClick={entwurfAngebotVerwerfen} className="py-3 bg-slate-100 rounded-2xl font-semibold text-red-700">Verwerfen</button>
                    <button type="button" onClick={entwurfFortsetzen} className="py-4 bg-blue-600 text-white rounded-2xl font-semibold shadow-lg">Fortsetzen</button>
                </div>
            </div>
        )}

        {/* „Fertig“, während noch gespeichert wird: schliesst danach von selbst. */}
        {schliessenGewuenscht && laufendeSpeicherungen > 0 && (
            <div role="status" aria-live="polite" className="fixed bottom-0 left-0 right-0 z-10 bg-white p-4 border-t border-slate-200 shadow-lg pb-safe space-y-3">
                <p className="font-semibold text-slate-800 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Wird noch gespeichert …</p>
                <p className="text-xs text-slate-500 text-center">Der Assistent schließt von selbst, sobald alles gespeichert ist.</p>
                <div className="grid gap-3">
                    {notausgang && (
                        <button type="button" onClick={onCancel} className="py-3 bg-slate-100 rounded-2xl font-semibold text-slate-700">Trotzdem schließen – im Hintergrund weiter</button>
                    )}
                    <button type="button" onClick={weiterErfassen} className="py-4 bg-blue-600 text-white rounded-2xl font-semibold shadow-lg">Weiter erfassen</button>
                </div>
            </div>
        )}

        {kameraOffen && (
            <VerortungsKamera
                verortung={projektVerortung}
                plaene={plans}
                sitzungSchluessel={verortungsSchluessel}
                titel="Foto zum Eintrag"
                restFotos={maxFotos - photos.length}
                onAufnahme={kameraAufnahme}
                // Ausweg immer da: Systemkamera wie bisher, im selben Tipp (sonst blockt der Browser den Dateidialog).
                // Davor sofort sichern — die Systemkamera ist der Moment, in dem Android die App beendet.
                onSystemkamera={() => { setKameraOffen(false); entwurfSofortSichern(); cameraInputRef.current?.click(); }}
                onSchliessen={() => setKameraOffen(false)}
            />
        )}

        {editingPhotoIndex !== null && photos[editingPhotoIndex] && (
            <ImageEditor
                initialImage={photos[editingPhotoIndex]}
                onSave={(newImage) => {
                    setPhotos(prev => prev.map((p, i) => i === editingPhotoIndex ? newImage : p));
                    setEditingPhotoIndex(null);
                }}
                onClose={() => setEditingPhotoIndex(null)}
            />
        )}

        {/* Verortung auf Plan: bleibt offen, jeder Tipp = weitere Nadel, heraus ueber „Fertig“.
            Liegt IN der Flaeche des Assistenten (Zurueck-Taste, siehe flaecheRef). Oberflaeche
            deutsch wie der ganze Assistent. */}
        {showPlanPicker && (
            <PlanVerortungDialog
                imPortal={false}
                plaene={dialogPlaene}
                startPlanId={pinAnzahl > 0 ? null : live.livePlanId}
                pins={pinsVon(data)}
                onPinSetzen={handlePinSetzen}
                onPinEntfernen={handlePinEntfernen}
                onFertig={() => setShowPlanPicker(false)}
                planAnlegen={planAnlegen}
                live={live}
                onLiveUebernehmen={liveUebernehmen}
                // Tipp auf den Plan eines Geschosses = Korrektur „hier bin ich“ (neuer Nullpunkt).
                onPlanGetippt={live.planBestaetigen}
            />
        )}
    </div>
  );
};
